import { computed, readonly, ref, watch } from 'vue';
import { activeApplicationId } from '@/router';

import useArtifacts from '@/composables/useArtifacts';
import useGlueCharmServices from './useGlueCharmServices';
import usePersistentData from '@/composables/usePersistentData';
import useLongRunningRequests from '@/composables/useLongRunningRequests';

import { ApplicationArtifactTypes, ArtifactTypes } from '@/constants/artifacts';

const { fetchGlueCharmMethod } = useGlueCharmServices();
const { savePersistentData, getPersistentData } = usePersistentData();
const { getAllLongRunningRequests } = useLongRunningRequests();

const { artifacts, cleanArtifactsByType, setArtifacts } = useArtifacts({
  onUpdateApplication: () => {
    updateApplicationPersistentData(activeApplicationId.value);
  },
});

const readOnlyMode = ref();
const lastCreatedApplicationId = ref();

const activeApplication = computed(() => activeApplicationId.value &&  {
  ...artifacts[ArtifactTypes.Application][activeApplicationId.value],
});

function updateApplicationPersistentData(appId) {
  const newPersistentData = ApplicationArtifactTypes.reduce((acc, artifactType) => {
    acc[artifactType] = Object.values(artifacts[artifactType]);
    return acc;
  }, {});
  savePersistentData(appId, newPersistentData);
}

async function getAllApplicationArtifacts(appId) {
  const cacheArtifacts = await getPersistentData(appId);

  if (cacheArtifacts) {
    Object.keys(cacheArtifacts).forEach(artifactType => {
      cleanArtifactsByType(artifactType);
      setArtifacts(artifactType, cacheArtifacts[artifactType] || []);
    });
  }

  await getAllLongRunningRequests(appId);
}

async function getFullApplicationContent() {
  await getAllApplicationArtifacts(activeApplicationId.value);
}

async function createLegacyApplicationClone(appId, workspaceId, name, description) {
  const created = await fetchGlueCharmMethod('application-manager/create', 'createApplicationClone', {
    id: appId,
    organization_id: workspaceId,
    shortname: name.slice(0, 5),
    name,
    description,
  });
  return created;
}

watch(activeApplicationId, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal) {
    getFullApplicationContent();
  }
}, {immediate: true});

export default function useApplications() {
  return {
    readOnlyMode: readonly(readOnlyMode),
    lastCreatedApplicationId: readonly(lastCreatedApplicationId),
    activeApplication,
    getFullApplicationContent,
    createLegacyApplicationClone,
  };
}
